// src/components/ui/Button.jsx
//
// Primitives layer, phase 1 (Frontend Completeness & UI/UX Unification
// Audit, Part 4). Matches the app's already-established visual language --
// the brand.cta violet (#7c3aed / hover #6d28d9) used for every primary
// button on the homepage/auth pages/dashboard, and a deeper brand-violet
// convention (matching SearchBar.jsx) used for secondary "action" CTAs
// like Create Group. Not inventing a new look, formalizing the one that
// already exists in a dozen bespoke implementations.
import React from 'react';
import { Loader2 } from 'lucide-react';

const VARIANT_CLASSES = {
  // The app's actual primary CTA color everywhere (Login/Register submit,
  // homepage hero) -- brand.cta from tailwind.config.js.
  primary:
    'bg-[#7c3aed] hover:bg-[#6d28d9] text-white shadow-lg disabled:hover:bg-[#7c3aed]',
  // Was Tailwind's generic `purple-700`/`600` -- a visibly different,
  // more magenta hue than Stocker's actual brand violet (#7c3aed, `primary`
  // above). Confirmed 2026-09-11 this was never a deliberate second brand
  // color, just Tailwind's default palette used ad hoc in the original
  // Create Group/search implementations this primitive formalized. Moved
  // onto the real brand hue -- a deeper violet than `primary` so the two
  // variants still read as visually distinct.
  secondary:
    'bg-violet-800 hover:bg-violet-700 text-white shadow-sm disabled:hover:bg-violet-800',
  ghost:
    'bg-transparent hover:bg-cream-900/5 dark:hover:bg-white/10 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-white/10',
  danger:
    'bg-red-600 hover:bg-red-500 text-white shadow-sm disabled:hover:bg-red-600',
};

const SIZE_CLASSES = {
  sm: 'px-3 py-1.5 text-sm rounded-lg gap-1.5',
  md: 'px-4 py-2.5 text-sm rounded-xl gap-2',
  lg: 'px-6 py-4 text-base rounded-2xl gap-2',
};

/**
 * @param {'primary'|'secondary'|'ghost'|'danger'} variant
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean} loading - shows a spinner and disables the button, keeps its width stable
 * @param {boolean} fullWidth
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled = false,
  className = '',
  children,
  ...rest
}) {
  return (
    <button
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center font-semibold transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ${
        VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary
      } ${SIZE_CLASSES[size] || SIZE_CLASSES.md} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}
